<template>
  <section class="hero">
    <div class="hero-content">
      <div class="badge reveal">✨ Revolutionizing Business Admin</div>
      <h1 class="reveal delay-1">
        Your AI Assistant That Actually<br /><span
          >Understands Your Business</span
        >
      </h1>
      <p class="subtitle reveal delay-2">
        Stop juggling admin tasks. Start growing your revenue. <br /><strong
          >Save 10+ hours every week</strong
        >
        with intelligent automation.
      </p>

      <div class="cta-group reveal delay-3">
        <a href="#waitlist" class="cta-button">Join the Waitlist</a>
        <p class="trust-line">🔒 Private. Secure. Built for growth.</p>
      </div>
    </div>

    <div class="hero-visual">
      <div class="demo-grid">
        <div class="demo-card glass-card reveal delay-1">
          <div class="card-header">
            <span class="icon">⚡</span>
            <h3>Operational Excellence</h3>
          </div>
          <p class="card-desc">Handling inquiries 24/7</p>
          <div class="chat-mockup">
            <img
              src="../assets/images/pabu-chat-demo.gif"
              alt="Bizzento Chat"
              class="chat-img"
            />
          </div>
        </div>

        <div class="demo-card glass-card reveal delay-2">
          <div class="card-header">
            <span class="icon">🧠</span>
            <h3>Strategic Growth</h3>
          </div>
          <p class="card-desc">Real-time business insights</p>
          <div class="dashboard-mockup">
            <div class="metric">
              <span class="label">Revenue</span>
              <span class="value">$4,800 <small>↑ 22%</small></span>
            </div>
            <div class="metric">
              <span class="label">Bookings</span>
              <span class="value">32 <small>↑ 15%</small></span>
            </div>
            <div class="insight-pill">
              💡 Trend: High interest in "Chocolate Cake"
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.hero {
  /* Using your theme variables */
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  color: var(--dark-accent);

  /* Applying your specific gradient background */
  background-color: var(--bg-warm-base);
  background-image: radial-gradient(
      at 0% 0%,
      var(--sunlight-glow) 0%,
      transparent 55%
    ),
    radial-gradient(at 100% 100%, var(--soft-gold) 0%, transparent 55%),
    radial-gradient(at 50% 50%, #f7f2e4 0%, transparent 100%);
}

.hero-content {
  max-width: 900px;
  margin-bottom: 60px;
}

.badge {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid var(--soft-gold);
  border-radius: 100px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 24px;
}

h1 {
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.1;
  margin-bottom: 24px;
  font-weight: 800;
}

h1 span {
  color: #bfa14d; /* Subtle gold accent */
}

.subtitle {
  font-size: 1.25rem;
  color: var(--text-muted);
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 40px;
}

.cta-button {
  display: inline-block;
  background: var(--dark-accent);
  color: white;
  padding: 18px 42px;
  border-radius: 12px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
}

.trust-line {
  margin-top: 16px;
  font-size: 0.85rem;
  color: var(--text-muted);
}

/* Visuals Section */
.hero-visual {
  width: 100%;
  max-width: 1100px;
}

.demo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
}

.glass-card {
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  padding: 32px;
  text-align: left;
  transition: transform 0.3s ease;
}

.glass-card:hover {
  transform: translateY(-10px);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.card-desc {
  color: var(--text-muted);
  margin-bottom: 24px;
  font-size: 0.95rem;
}

/* Dashboard Mockup Styles */
.metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.metric .label {
  font-weight: 500;
}
.metric .value {
  font-weight: 700;
  color: #2d6a4f;
}
.metric small {
  color: #52b788;
  font-size: 0.75rem;
}

.insight-pill {
  margin-top: 20px;
  background: var(--sunlight-glow);
  padding: 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #856404;
}

.chat-img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .hero {
    padding: 60px 20px;
  }
  .demo-grid {
    grid-template-columns: 1fr;
  }
}
</style>
<script>
export default {
  name: 'HeroSection',
};
</script>
